import { signup } from "@/api/endpoints/signup";
import GoogleIcon from "@/assets/icons/google.svg";
import MicrosoftIcon from "@/assets/icons/microsoft.svg";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import {
  PUBLIC_EN_SITE_DATA_POLICY,
  PUBLIC_EN_SITE_PRIVACY_POLICY,
  PUBLIC_EN_SITE_TERMS,
  ROUTES,
} from "@/routes_new";
import { useSignupFormSchema } from "@/views/shared/useSignupFormSchema";
import AllEarsTitle from "@components/AllEarsTitle";
import SignupImage from "@components/SignupImage";
import { Button } from "@components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@components/ui/input-group";
import { Separator } from "@components/ui/separator";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { EyeClosedIcon, EyeIcon } from "lucide-react";
import { stringifyUrl } from "@/utils";
import { CLOUDFLARE_TURNSTILE_SITE_KEY } from "@/constants";
import { useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { applyServerErrors } from "@/utils/forms";
import { useSignupContext, useSignupRoutes } from "../context";
import PublicLogo from "@components/ui/public-logo";

export default function SignupInfo() {
  const { t } = useLingui();

  // URL state
  const history = useHistory();
  const { language } = useParams<{ language: string }>();

  // Context
  const {
    companyName,
    fullCompanyName,
    companyDescription,
    websiteUrl,
    useCases,
    customUseCase,
    setEmail,
  } = useSignupContext();
  const signupRoutes = useSignupRoutes();

  const formSchema = useSignupFormSchema({ requireTurnstile: true });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      turnstileToken: "",
    },
  });

  const { mutateAsync: mutateSignup } = useMutation({
    mutationFn: signup,
    onSuccess: async (res) => {
      if (res.ok) {
        setEmail(res.data.email);
        history.push(signupRoutes.SIGNUP_CONFIRM);
        return;
      }

      if (res.status === 400 && res.data) {
        applyServerErrors(res.data, form, { name: "firstName" });
        return;
      }

      throw new Error();
    },
    onError: () => {
      toast.error(t`Hmm... Någonting gick snett`);
    },
  });

  const [isShowingPassword, setIsShowingPassword] = useState<boolean>(false);
  const showSpinner = useDelayedLoading(form.formState.isSubmitting);

  // Side-effects
  const turnstileRendered = useRef(false);
  useEffect(() => {
    if (turnstileRendered.current) return;
    turnstileRendered.current = true;

    turnstile.render("#turnstile-container", {
      sitekey: CLOUDFLARE_TURNSTILE_SITE_KEY,
      callback: function (token) {
        form.setValue("turnstileToken", token);
      },
      "error-callback": () => {
        form.setValue("turnstileToken", "");
      },
      size: "flexible",
      appearance: "interaction-only",
    });
  }, [form]);

  // Return to index signup route if missing company name or search type
  if (!companyName) {
    return <Redirect to={signupRoutes.SIGNUP} />;
  }

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    return mutateSignup({
      name: [values.firstName, values.lastName].join(" "),
      turnstileToken: values.turnstileToken!,
      organization_name: companyName,
      password: values.password,
      email: values.email,
      language,
      company_name: companyName,
      full_company_name: fullCompanyName,
      company_description: companyDescription,
      onboarding_url: websiteUrl,
      use_cases: useCases,
      custom_use_case: customUseCase,
    });
  };

  return (
    <>
      <AllEarsTitle title={t`Skapa konto`} />

      <div className="md:h-svh grid md:grid-cols-2">
        <div className="p-7 md:p-14 flex flex-col gap-[15svh] overflow-y-auto h-full">
          <PublicLogo width={80} className="self-start" />

          <div className="w-full space-y-6 max-w-lg mx-auto">
            <div className="flex flex-col items-center text-balance text-center gap-1.5">
              <h1 className="text-2xl font-semibold">
                <Trans>Få notiser om nya omnämnanden</Trans>
              </h1>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Testa All Ears gratis i 7 dagar – inget kreditkort krävs.
                </Trans>
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <Button type="button" variant="outline" asChild>
                <a
                  href={stringifyUrl({
                    url: ROUTES.SIGNUP_SSO_GOOGLE,
                    query: {
                      language,
                      company_name: companyName,
                      full_company_name: fullCompanyName,
                      company_description: companyDescription,
                      onboarding_url: websiteUrl,
                      use_cases: useCases?.join(","),
                      custom_use_case: customUseCase,
                    },
                  })}
                >
                  <GoogleIcon />
                  <Trans>Skapa konto med Google</Trans>
                </a>
              </Button>

              <Button type="button" variant="outline" asChild>
                <a
                  href={stringifyUrl({
                    url: ROUTES.SIGNUP_SSO_AZURE,
                    query: {
                      language,
                      company_name: companyName,
                      full_company_name: fullCompanyName,
                      company_description: companyDescription,
                      onboarding_url: websiteUrl,
                      use_cases: useCases?.join(","),
                      custom_use_case: customUseCase,
                    },
                  })}
                >
                  <MicrosoftIcon />
                  <Trans>Skapa konto med Microsoft</Trans>
                </a>
              </Button>
            </div>

            <div className="flex gap-2 items-center">
              <Separator className="flex-1" />
              <p className="text-xs text-muted-foreground text-center uppercase tracking-wide">
                <Trans>Eller</Trans>
              </p>
              <Separator className="flex-1" />
            </div>

            <form
              className="flex flex-col gap-6"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FieldGroup className="gap-5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                  <Controller
                    control={form.control}
                    name="firstName"
                    render={({ field, fieldState }) => {
                      const id = "first-name";

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Förnamn</Trans>
                          </FieldLabel>

                          <Input
                            {...field}
                            id={id}
                            autoComplete="given-name"
                            aria-invalid={fieldState.invalid}
                            placeholder={t`Jane`}
                          />

                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    control={form.control}
                    name="lastName"
                    render={({ field, fieldState }) => {
                      const id = "last-name";

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Efternamn</Trans>
                          </FieldLabel>

                          <Input
                            {...field}
                            id={id}
                            autoComplete="family-name"
                            aria-invalid={fieldState.invalid}
                            placeholder={t`Smith`}
                          />

                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />
                </div>

                <Controller
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => {
                    const id = "email";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Ange din e-postadress för arbetet</Trans>
                        </FieldLabel>

                        <Input
                          {...field}
                          id={id}
                          autoComplete="work email"
                          aria-invalid={fieldState.invalid}
                          placeholder={t`namn@foretag.com`}
                        />

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  control={form.control}
                  name="password"
                  render={({ field, fieldState }) => {
                    const id = "password";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Lösenord</Trans>
                        </FieldLabel>

                        <InputGroup>
                          <InputGroupInput
                            {...field}
                            id={id}
                            autoComplete="new-password"
                            placeholder="•••••••••••••"
                            aria-invalid={fieldState.invalid}
                            type={isShowingPassword ? "text" : "password"}
                          />

                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              type="button"
                              onClick={() => {
                                setIsShowingPassword((prev) => !prev);
                              }}
                            >
                              {isShowingPassword ? (
                                <EyeClosedIcon />
                              ) : (
                                <EyeIcon />
                              )}
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>

                        {fieldState.invalid ? (
                          <FieldError errors={[fieldState.error]} />
                        ) : !field.value ? (
                          <FieldDescription>
                            <Trans>Minst 8 tecken</Trans>
                          </FieldDescription>
                        ) : null}
                      </Field>
                    );
                  }}
                />

                <Controller
                  control={form.control}
                  name="turnstileToken"
                  render={({ field, fieldState }) => (
                    <Field
                      data-invalid={fieldState.invalid}
                      data-testid="turnstile-field"
                      data-ready={Boolean(field.value)}
                    >
                      <FieldLabel className="sr-only">
                        <Trans>Bekräfta att du är en människa</Trans>
                      </FieldLabel>
                      <div id="turnstile-container"></div>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              </FieldGroup>

              <Button disabled={showSpinner.shouldRender} type="submit">
                <Trans>Starta 7-dagars provperiod</Trans>
                {showSpinner.isVisible && <Spinner />}
              </Button>

              <p className="text-center text-sm text-muted-foreground text-balance">
                <Trans>
                  Genom att fortsätta godkänner du våra{" "}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={PUBLIC_EN_SITE_TERMS}
                    className="underline hover:text-foreground transition"
                  >
                    användarvillkor
                  </a>
                  , vår{" "}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={PUBLIC_EN_SITE_PRIVACY_POLICY}
                    className="underline hover:text-foreground transition"
                  >
                    integritetspolicy
                  </a>{" "}
                  och vårt{" "}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={PUBLIC_EN_SITE_DATA_POLICY}
                    className="underline hover:text-foreground transition"
                  >
                    personuppgiftsbiträdesavtal
                  </a>
                  .
                </Trans>
              </p>
            </form>
          </div>
        </div>

        <SignupImage />
      </div>
    </>
  );
}
